import { useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ONBOARDING_PRESENCE_PAGE_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useIsProductAdmin } from '@confluence/current-user';
import { useAccessStatus } from '@confluence/session-data';
import { fg } from '@confluence/feature-gating';
import { TEAM_PRESENCE_CHANGEBOARDING_DB_EXPERIENCE } from '@confluence/experience-tracker';

import {
	type PresenceChangeboardingFeatureResponse,
	type PresenceChangeboardingFeatureProps,
	ChangeboardingState,
} from './PresenceChangeboarding';
import image from './assets/images/presence-spotlight-img.png';
// eslint-disable-next-line import/no-cycle
import { useChangeboardingSharedEvents } from './useChangeboardingSharedEvents';

export const i18n = defineMessages({
	spotlightTitle: {
		id: 'team-presence.avatar-group.spotlight.title',
		defaultMessage:
			'{contentType, select, database {See who’s on your database} other {See who’s on your content}}',
		description:
			'Title for a changeboarding spotlight shown to users when they first enter a piece of content to teach them about presence. {contentType} is the contents type',
	},
	spotlightDescriptionForUsers: {
		id: 'team-presence.avatar-group.spotlight.description-user',
		defaultMessage:
			'{contentType, select, database {You can now see who’s viewing this database in real time.} other {You can now see who’s viewing this content in real time.}}',
		description:
			'Description for a changeboarding spotlight shown to users when they first enter a piece of content to teach them about presence. {contentType} is the contents type',
	},
	spotlightDescriptionForAdmin: {
		id: 'team-presence.avatar-group.spotlight.description-admin',
		defaultMessage:
			'{contentType, select, database {Your team can now see who’s viewing this database in real time.} other {Your team can now see who’s viewing this content in real time.}}',
		description:
			'Description for a changeboarding spotlight shown to admins when they first enter a piece of content to teach them about presence. {contentType} is the contents type',
	},
	spotlightAccessibleLabel: {
		id: 'team-presence.avatar-group.spotlight.label',
		defaultMessage: 'Onboarding for real-time presence avatar group',
		description:
			'Accessible label for a changeboarding spotlight shown to users when they first enter a piece of content to teach them about presence.',
	},
	spotlightPrimaryAction: {
		id: 'team-presence.avatar-group.spotlight.primary-action',
		defaultMessage: 'OK',
		description:
			'Button text for a primary action on a changeboarding spotlight to acknowledge that you read the spotlight',
	},
});

const FEATURE_NAME = 'database';
// TODO: This must be the dynamic analytics source
// https://product-fabric.atlassian.net/browse/MDS-1330
export const DATABASE_AVATAR_GROUP_ANALYTIC_SOURCE = 'dbPresenceAvatarGroup';

type UseDatabaseChangeboarding = (
	args: PresenceChangeboardingFeatureProps,
) => PresenceChangeboardingFeatureResponse;

export const useDatabaseChangeboarding: UseDatabaseChangeboarding = ({
	contentId,
	contentMode,
	contentType,
	totalParticipants,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setOnboardingState, error: setError } = useSetOnboardingState();
	const { isProductAdmin } = useIsProductAdmin();
	const { isLicensed, isGuest } = useAccessStatus();
	const hasMoreThanOneParticipant = Boolean(totalParticipants && totalParticipants > 1);
	const isEligible =
		contentType === 'database' &&
		(isLicensed || isGuest) &&
		(isProductAdmin || hasMoreThanOneParticipant) &&
		// TODO: While removing FG, add new user check based on firstActiveDate like in usePresenceChangeboarding
		fg('confluence_team_presence_database');
	const {
		data: getData,
		error: getError,
		loading: getLoading,
	} = useGetOnboardingState(
		ONBOARDING_PRESENCE_PAGE_STATE_KEYS.PRESENCE_AVATAR_GROUP_DB_SPOTLIGHT_SEEN,
		!isEligible,
	);
	const { databasePresenceAvatarGroupSpotlightSeen: isSeen } = deserializeState(getData);
	const serverError = setError || getError;
	const serverLoading = getLoading;

	const [state, setState] = useState<ChangeboardingState>(
		isEligible ? ChangeboardingState.STARTED : ChangeboardingState.INITIAL,
	);

	useEffect(() => {
		if (serverLoading) {
			return;
		}

		if (serverError) {
			setState(ChangeboardingState.FAILED);
			return;
		}

		if (isEligible && !isSeen) {
			setState(ChangeboardingState.READY);
			return;
		}

		setState(ChangeboardingState.ABORTED);
	}, [serverLoading, isEligible, isSeen, serverError]);

	useChangeboardingSharedEvents({
		contentId,
		contentMode,
		contentType,
		analyticsSource: DATABASE_AVATAR_GROUP_ANALYTIC_SOURCE,
		featureName: FEATURE_NAME,
		experienceName: TEAM_PRESENCE_CHANGEBOARDING_DB_EXPERIENCE,
		state,
		error: serverError,
	});

	const handleComplete = () => {
		setState(ChangeboardingState.COMPLETED);
		void setOnboardingState({
			key: ONBOARDING_PRESENCE_PAGE_STATE_KEYS.PRESENCE_AVATAR_GROUP_DB_SPOTLIGHT_SEEN,
			value: 'true',
		});
	};

	const handleClose = () => {
		handleComplete();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: `${DATABASE_AVATAR_GROUP_ANALYTIC_SOURCE}Spotlight`,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'presenceSpotlightClose',
				attributes: {
					isProductAdmin,
				},
			},
		}).fire();
	};

	const handlePulseClick = () => {
		setState(ChangeboardingState.ACTIVE);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: `${DATABASE_AVATAR_GROUP_ANALYTIC_SOURCE}Pulse`,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'presencePulse',
				attributes: {
					isProductAdmin,
				},
			},
		}).fire();
	};

	const heading = formatMessage(i18n.spotlightTitle, { contentType });
	const label = formatMessage(i18n.spotlightAccessibleLabel);
	const descriptionI18n = isProductAdmin
		? i18n.spotlightDescriptionForAdmin
		: i18n.spotlightDescriptionForUsers;
	const description = formatMessage(descriptionI18n, { contentType });
	const actions = [
		{
			onClick: handleClose,
			text: formatMessage(i18n.spotlightPrimaryAction),
			testId: 'presence-spotlight-action-ok',
		},
	];

	return {
		featureName: FEATURE_NAME,
		state,
		error: serverError,
		content: {
			image,
			actions,
			heading,
			label,
			description,
			onPulseClick: handlePulseClick,
		},
	};
};
